// Captures the Android/Chromium `beforeinstallprompt` event at boot so the
// install modal can offer a real Install button later. Created once at startup;
// no-ops on browsers that never fire the event (iOS, desktop Safari, Firefox).

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallPrompt {
  /** True once the browser has offered an installable prompt we stashed. */
  available(): boolean;
  /** Show the native install prompt; resolves with the user's choice. */
  prompt(): Promise<InstallOutcome>;
}

export function createInstallPrompt(target: EventTarget = globalThis): InstallPrompt {
  let deferred: BeforeInstallPromptEvent | null = null;
  target.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // suppress Chrome's mini-infobar; we drive the prompt ourselves
    deferred = e as BeforeInstallPromptEvent;
  });
  return {
    available: () => deferred !== null,
    async prompt() {
      if (!deferred) return "unavailable";
      const e = deferred;
      deferred = null; // single-use, per spec
      e.prompt();
      const { outcome } = await e.userChoice;
      return outcome;
    },
  };
}
